import { useEffect, useCallback, useState, useRef, RefObject } from 'react';
import {
  registerUnmutedVideo,
  unregisterUnmutedVideo,
} from '@/utils/media/videoMuteCoordinator';
import {
  activatePriorityVideo,
  deactivatePriorityVideo,
} from '@/utils/media/videoPauseCoordinator';
import isElementVisible from '@/utils/dom/isElementVisible';
import safeTryPlayVideo from '@/utils/media/safeTryPlayVideo';
import useVideoAudioDetection from '@/hooks/useVideoAudioDetection';
import { CARD_MAX_STALL_RETRIES } from '@/consts/video';
import { PreviewMediaType } from '@/types';

import type { Preset } from '@/types';

type UseVideoPlaybackOptions = {
  videoRef: RefObject<HTMLVideoElement>;
  selectedPreset: Preset | null;
  isVideo: boolean;
};

const useVideoPlayback = ({
  videoRef,
  selectedPreset,
  isVideo,
}: UseVideoPlaybackOptions) => {
  const [isVideoMuted, setIsVideoMuted] = useState(true);
  const shouldPlayRef = useRef(false);

  const hasVideoPreset =
    selectedPreset?.type === PreviewMediaType.VIDEO &&
    selectedPreset.video?.length > 0 &&
    selectedPreset.video[0].media?.url;

  const { hasAudio, handleVideoLoadedMetadata: detectAudioOnLoadedMetadata } =
    useVideoAudioDetection({
      videoRef,
      isDetectionEnabled: isVideo,
      videoSourceId: selectedPreset?.flowId,
    });

  // Reset mute state when preset changes
  useEffect(() => {
    setIsVideoMuted(true);
    shouldPlayRef.current = false;
  }, [selectedPreset?.flowId]);

  // Activate priority video mode
  useEffect(() => {
    if (hasVideoPreset) {
      activatePriorityVideo();
    }
    return () => {
      if (hasVideoPreset) {
        deactivatePriorityVideo();
      }
    };
  }, [hasVideoPreset]);

  // Mute coordination
  const muteThisVideo = useCallback(() => setIsVideoMuted(true), []);

  const handleVideoMuteToggle = useCallback(() => {
    setIsVideoMuted((prev) => {
      const newMuted = !prev;
      if (!newMuted) {
        registerUnmutedVideo(muteThisVideo);
      } else {
        unregisterUnmutedVideo(muteThisVideo);
      }
      return newMuted;
    });
  }, [muteThisVideo]);

  useEffect(() => {
    return () => {
      unregisterUnmutedVideo(muteThisVideo);
    };
  }, [muteThisVideo]);

  // Intersection observer for video playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasVideoPreset) return;

    const tryPlay = () => {
      safeTryPlayVideo(video);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        shouldPlayRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          tryPlay();
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(video);

    const initialPlayTimeout = setTimeout(() => {
      if (isElementVisible(video)) {
        shouldPlayRef.current = true;
        tryPlay();
      }
    }, 100);

    return () => {
      clearTimeout(initialPlayTimeout);
      observer.disconnect();
      shouldPlayRef.current = false;
    };
  }, [videoRef, hasVideoPreset]);

  // Play recovery: handles both stalled videos and videos that never started
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasVideoPreset) return;

    let stallRetries = 0;
    let hasReloaded = false;

    const interval = setInterval(() => {
      const rs = video.readyState;
      const { paused } = video;

      // Video should be playing but is still paused (e.g. initial play() failed
      // because data wasn't buffered yet on some mobile devices)
      if (paused && shouldPlayRef.current && rs >= 2) {
        safeTryPlayVideo(video);
        return;
      }

      // iOS stall recovery: video started but got stuck
      const isStalled = !paused && rs < 3;

      if (isStalled && stallRetries < CARD_MAX_STALL_RETRIES) {
        stallRetries++;
        video.currentTime = video.currentTime || 0;
        safeTryPlayVideo(video);
      } else if (isStalled && !hasReloaded) {
        hasReloaded = true;
        video.load();
        // iOS Safari can re-enable native controls after .load() resets state
        video.controls = false;
        setTimeout(() => {
          safeTryPlayVideo(video);
        }, 100);
      } else if (rs >= 3) {
        stallRetries = 0;
      }
    }, 500);

    return () => clearInterval(interval);
  }, [videoRef, hasVideoPreset]);

  const handleVideoLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      detectAudioOnLoadedMetadata();
      safeTryPlayVideo(video);
    }
  }, [videoRef, detectAudioOnLoadedMetadata]);

  return {
    isVideoMuted,
    hasAudio,
    handleVideoMuteToggle,
    handleVideoLoadedMetadata,
  };
};

export default useVideoPlayback;
